import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOrganizerAuth } from "../contexts/organizer-auth-context";
import { Heart, Mail, Loader2, ArrowRight, HelpCircle, Moon, Sun, UserPlus } from "lucide-react";
import { useTurnstile } from "../hooks/use-turnstile";
import { useTheme } from "../hooks/use-theme";
import { organizerAuthApi } from "../lib/api";
import { paths } from "../lib/paths";
import { AppLink } from "../lib/route-primitives";
import { cn } from "../lib/utils";
import { WEB_PRIVACY_PATH, WEB_TERMS_PATH } from "@wedding/shared";

/** Misafir uygulamasının kökü — KVKK aydınlatma metni orada (`/privacy`). */
const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

function ThemeToggleButton({ isDark, toggle }: { isDark: boolean; toggle: () => void }) {
  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
      className={cn(
        "relative h-6 w-11 rounded-full border-2 transition-all duration-300 cursor-pointer shrink-0",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:opacity-80 hover:scale-105 motion-reduce:hover:scale-100",
        isDark ? "bg-primary/30 border-primary/50" : "bg-amber-200 border-amber-300"
      )}
    >
      <span className={cn(
        "absolute top-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center",
        isDark ? "translate-x-[18px] bg-primary" : "translate-x-0.5 bg-amber-400"
      )}>
        {isDark ? <Moon className="h-2.5 w-2.5 text-white" /> : <Sun className="h-2.5 w-2.5 text-white" />}
      </span>
    </button>
  );
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  return `${local.slice(0, 1)}***@${domain}`;
}

/** Sunucu hata kodu → çeviri anahtarı (giriş + kayıt). */
const AUTH_ERRORS: Record<string, string> = {
  signup_closed: "organizerAuth.signupClosed",
  disposable_email: "organizerAuth.disposableEmail",
  email_alias_exists: "organizerAuth.emailAliasExists",
  too_many_signups: "organizerAuth.tooManySignups",
  "Organizatör bulunamadı": "organizerAuth.accountNotFound",
};

/**
 * Organizatör girişi + kaydı (2026-10-15).
 * - Giriş ASLA hesap açmaz; yanıt nötr ("hesabınız varsa kodu gönderdik").
 * - `mode="signup"` (`/signup`): yalnız admin kaydı açtıysa form; ad + koşul onayı →
 *   e-posta kodu → hesap kod doğrulanınca açılır. Kapalıysa "teklif al" yönlendirmesi.
 */
export function OrganizerLoginPage({ mode = "login" }: { mode?: "login" | "signup" }) {
  const { t } = useTranslation();
  const { requestCode, verifyCode } = useOrganizerAuth();
  const isSignup = mode === "signup";
  /** `null` → henüz bilinmiyor (bağlantı çizilmez). */
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);
  useEffect(() => {
    organizerAuthApi
      .signupStatus()
      .then((res) => setSignupOpen(res.data?.open === true))
      .catch(() => setSignupOpen(false));
  }, []);
  const [name, setName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const { isDark, toggle: toggleTheme } = useTheme();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [bgUrl, setBgUrl] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/organizer/theme", { credentials: "include" })
      .then((r) => r.json())
      .then((data: { ok: boolean; data?: { bg_organizer_auth: string | null } }) => {
        if (data.ok && data.data?.bg_organizer_auth) {
          setBgUrl(data.data.bg_organizer_auth);
        }
      })
      .catch(() => {});
  }, []);

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { containerRef: turnstileRef, token: turnstileToken, error: turnstileError, reset: resetTurnstile } = useTurnstile();
  const digitRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  const otpCode = digits.join("");

  const errorText = (err: unknown) => {
    const msg = err instanceof Error ? err.message : "";
    return AUTH_ERRORS[msg] ? t(AUTH_ERRORS[msg]) : msg || t("common.unknownError");
  };

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!turnstileToken) {
      setError(t("common.error"));
      return;
    }
    if (isSignup && (!name.trim() || !accepted)) {
      setError(t("organizerAuth.signupMissing"));
      return;
    }
    setLoading(true);
    try {
      const normalized = email.trim().toLowerCase();
      if (isSignup) {
        await organizerAuthApi.signup({ name: name.trim(), email: normalized, acceptTerms: true, turnstileToken });
      } else {
        await requestCode(normalized, turnstileToken);
      }
      resetTurnstile();
      setStep("code");
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    } catch (err: unknown) {
      setError(errorText(err));
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyCode(email.trim().toLowerCase(), otpCode);
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }

  function handleDigitChange(index: number, value: string) {
    const clean = value.replace(/\D/g, "");
    if (!clean) return;
    const next = [...digits];
    next[index] = clean[clean.length - 1];
    setDigits(next);
    if (index < 5) digitRefs.current[index + 1]?.focus();
  }

  function handleDigitKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
        if (index > 0) digitRefs.current[index - 1]?.focus();
      } else if (index > 0) {
        digitRefs.current[index - 1]?.focus();
      }
    }
  }

  function handleDigitPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = Array(6).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    digitRefs.current[Math.min(pasted.length, 5)]?.focus();
  }

  function goBack() {
    setStep("email");
    setDigits(Array(6).fill(""));
    setError("");
  }

  // ── Background — light & dark adaptive ────────────────────────
  const background = (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 bg-[#f7f7f7] dark:bg-[#1b1817]" />
      <div className="absolute inset-0" style={{ filter: "blur(80px)" }}>
        <div className="absolute top-[-15%] left-[5%] w-[65%] h-[65%] rounded-full bg-primary/15 dark:bg-primary/30" />
        <div className="absolute bottom-[-15%] right-[5%] w-[55%] h-[55%] rounded-full bg-primary/10 dark:bg-primary/20" />
        <div className="absolute top-[25%] right-[15%] w-[40%] h-[45%] rounded-full bg-primary/12 dark:bg-[rgba(180,130,95,0.22)]" />
        <div className="absolute top-[50%] left-[20%] w-[35%] h-[35%] rounded-full bg-primary/8 dark:bg-[rgba(120,80,60,0.18)]" />
      </div>
      <div className="absolute inset-0 bg-[#f7f7f7]/30 dark:bg-[#1b1817]/40" />
      <div
        className="absolute inset-0 opacity-15 dark:opacity-30"
        style={{
          backgroundImage: "radial-gradient(circle at 2px 2px, rgba(141,107,94,0.15) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
    </div>
  );

  // ── Brand hero ─────────────────────────────────────────────────
  const hero = (
    <div className="text-center space-y-3">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 mb-2">
        <Heart className="w-8 h-8 text-primary" />
      </div>
      <h1 className="font-serif text-4xl italic text-primary leading-tight">
        {t("organizerAuth.panelTitle")}
      </h1>
      <p className="text-sm text-foreground/50 dark:text-white/60">{t("organizerAuth.tagline")}</p>
    </div>
  );

  // ── Email step ─────────────────────────────────────────────────
  const inputCls =
    "w-full bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg py-3.5 px-4 text-base text-foreground placeholder:text-muted-foreground/50 transition-[border-color,box-shadow]";

  // Giriş ⇄ kayıt geçişi: kayıt açıksa "Hesap oluşturun", kapalıysa "Teklif alın" (landing formu).
  const switchLink = isSignup ? (
    <p className="text-center text-sm text-muted-foreground">
      {t("organizerAuth.haveAccount")}{" "}
      <AppLink to={paths.root} className="font-medium text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none cursor-pointer">
        {t("organizerAuth.loginCta")}
      </AppLink>
    </p>
  ) : signupOpen === null ? null : (
    <p className="text-center text-sm text-muted-foreground" data-testid="org-no-account">
      {t("organizerAuth.noAccount")}{" "}
      {signupOpen ? (
        <AppLink to={paths.signup} className="font-medium text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none cursor-pointer">
          {t("organizerAuth.signupCta")}
        </AppLink>
      ) : (
        <a href={`${WEB_BASE}/#contact`} className="font-medium text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none cursor-pointer">
          {t("organizerAuth.quoteCta")}
        </a>
      )}
    </p>
  );

  // Kayıt kapalıyken /signup: form yok, teklif yönlendirmesi.
  const signupClosedStep = (
    <div className="space-y-5" data-testid="org-signup-closed">
      <div>
        <h2 className="text-xl font-serif text-foreground mb-1">{t("organizerAuth.signupClosedTitle")}</h2>
        <p className="text-sm text-muted-foreground">{t("organizerAuth.signupClosedBody")}</p>
      </div>
      <a
        href={`${WEB_BASE}/#contact`}
        className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 rounded-lg transition-colors shadow-lg shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
      >
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
        <span>{t("organizerAuth.quoteCta")}</span>
      </a>
      {switchLink}
    </div>
  );

  const emailStep = (
    <form onSubmit={handleRequestCode} className="space-y-5" noValidate>
      <div>
        <h2 className="text-xl font-serif text-foreground mb-1">
          {isSignup ? t("organizerAuth.signupTitle") : t("organizerAuth.welcomeBack")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isSignup ? t("organizerAuth.signupPrompt") : t("organizerAuth.emailPrompt")}
        </p>
      </div>
      {isSignup && (
        <div className="space-y-1.5">
          <label htmlFor="org-name" className="block text-xs font-medium text-muted-foreground uppercase tracking-wider ml-0.5">
            {t("organizerAuth.nameLabel")}
          </label>
          <input
            id="org-name"
            name="name"
            type="text"
            autoComplete="name"
            autoFocus
            required
            maxLength={80}
            placeholder={t("organizerAuth.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
        </div>
      )}
      <div className="space-y-1.5">
        <label
          htmlFor="org-email"
          className="block text-xs font-medium text-muted-foreground uppercase tracking-wider ml-0.5"
        >
          {t("organizerAuth.emailLabel")}
        </label>
        <input
          id="org-email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus={!isSignup}
          required
          spellCheck={false}
          placeholder={t("organizerAuth.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-describedby={error ? "org-email-error" : undefined}
          className="w-full bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg py-3.5 px-4 text-base text-foreground placeholder:text-muted-foreground/50 transition-[border-color,box-shadow]"
        />
      </div>
      {isSignup && (
        <label className="flex items-start gap-2.5 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
          />
          <span>
            <a href={`${WEB_BASE}${WEB_TERMS_PATH}`} target="_blank" rel="noopener" className="underline underline-offset-2 hover:text-foreground">
              {t("organizerAuth.acceptTerms")}
            </a>
            {t("organizerAuth.acceptAnd")}
            <a href={`${WEB_BASE}${WEB_PRIVACY_PATH}`} target="_blank" rel="noopener" className="underline underline-offset-2 hover:text-foreground">
              {t("organizerAuth.acceptKvkk")}
            </a>
            {t("organizerAuth.acceptPost")}
          </span>
        </label>
      )}
      <div ref={turnstileRef} className="flex justify-center" />
      {(error || turnstileError) && (
        <p id="org-email-error" role="alert" className="text-sm text-red-500 dark:text-red-400">
          {error || turnstileError}
        </p>
      )}
      <button
        type="submit"
        disabled={loading || !email.trim() || !turnstileToken || (isSignup && (!name.trim() || !accepted))}
        className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        ) : isSignup ? (
          <UserPlus className="w-4 h-4" aria-hidden="true" />
        ) : (
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        )}
        <span>{isSignup ? t("organizerAuth.createAccount") : t("organizerAuth.sendCode")}</span>
      </button>
      {switchLink}
      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        {t("common.privacyPre")}
        <a
          href={`${WEB_BASE}${WEB_PRIVACY_PATH}`}
          target="_blank"
          rel="noopener"
          className="underline underline-offset-2 transition-colors hover:text-foreground"
        >
          {t("common.privacyLink")}
        </a>
        {t("common.privacyPost")}
      </p>
    </form>
  );

  // ── OTP step ───────────────────────────────────────────────────
  const otpStep = (
    <form onSubmit={handleVerifyCode} className="space-y-5" noValidate>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-serif text-foreground">{t("organizerAuth.verifyTitle")}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {/* Giriş yanıtı nötr: hesap yoksa kod gitmez, adrese bilgi e-postası gider. */}
          {t(isSignup ? "organizerAuth.signupVerifyHint" : "organizerAuth.verifyHint", { email: maskEmail(email) })}
        </p>
      </div>
      <div
        className="grid grid-cols-6 gap-2"
        role="group"
        aria-label={t("organizerAuth.verifyCode")}
        onPaste={handleDigitPaste}
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { digitRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            autoComplete={i === 0 ? "one-time-code" : "off"}
            value={digit}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleDigitKeyDown(i, e)}
            aria-label={`${t("organizerAuth.verifyCode")} ${i + 1}`}
            className="w-full h-14 bg-muted/40 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg text-center text-2xl font-bold text-foreground transition-[border-color,box-shadow]"
          />
        ))}
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || otpCode.length < 6}
        className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
        {loading ? t("auth.verifying") : t("organizerAuth.login")}
      </button>
      <button
        type="button"
        onClick={goBack}
        className="w-full text-sm text-muted-foreground hover:text-primary transition-colors py-2 cursor-pointer"
      >
        {t("organizerAuth.changeEmail")}
      </button>
    </form>
  );

  const formContent =
    step === "code" ? (
      otpStep
    ) : isSignup && signupOpen === null ? (
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" aria-label={t("common.loading")} />
    ) : isSignup && !signupOpen ? (
      signupClosedStep
    ) : (
      emailStep
    );

  // ── MOBILE LAYOUT ──────────────────────────────────────────────
  if (!isDesktop) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        {background}
        <div className="absolute top-4 right-4 z-20">
          <ThemeToggleButton isDark={isDark} toggle={toggleTheme} />
        </div>
        <main className="relative z-10 flex flex-col min-h-screen max-w-md mx-auto w-full px-6 pt-12 pb-8">
          {hero}
          <div className="bg-background/80 backdrop-blur-xl border border-primary/20 rounded-xl p-8 mt-10 shadow-xl shadow-primary/5">
            {formContent}
          </div>
          <div className="flex-1" />
          <footer className="text-center mt-4">
            <p className="text-foreground/25 dark:text-white/20 text-[10px] uppercase tracking-[0.2em]">
              {t("organizerAuth.secureGateway")}
            </p>
          </footer>
        </main>
        <div className="fixed bottom-6 right-6 z-20">
          <a
            href="mailto:destek@mertbal.com"
            className="w-12 h-12 bg-background/80 border border-primary/20 text-muted-foreground hover:text-primary rounded-full flex items-center justify-center shadow-lg transition-colors"
            aria-label={t("common.help")}
          >
            <HelpCircle className="w-5 h-5" />
          </a>
        </div>
      </div>
    );
  }

  // ── DESKTOP LAYOUT ─────────────────────────────────────────────
  return (
    <div className="relative min-h-screen overflow-hidden">
      {background}
      <div className="absolute top-5 right-6 z-20">
        <ThemeToggleButton isDark={isDark} toggle={toggleTheme} />
      </div>
      <div className="fixed -top-[10%] -right-[10%] w-[40%] h-[40%] bg-primary/8 dark:bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="fixed -bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/8 dark:bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
      <main className="relative z-10 min-h-screen flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[1000px] bg-background/80 backdrop-blur-xl border border-primary/20 rounded-xl overflow-hidden shadow-2xl shadow-primary/10 flex">
          {/* Left decorative panel */}
          <div className="w-1/2 relative min-h-[520px]">
            <div className="absolute inset-0 overflow-hidden bg-[#efe8e4] dark:bg-[#262220]">
              {bgUrl ? (
                <img
                  src={bgUrl}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div style={{ filter: "blur(60px)" }} className="absolute inset-0">
                  <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-primary/25 dark:bg-primary/40" />
                  <div className="absolute bottom-[-20%] right-[-20%] w-[70%] h-[70%] rounded-full bg-primary/15 dark:bg-primary/25" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/5 dark:bg-[#1b1817]/40" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 dark:from-[#1b1817]/80 to-transparent" />
            <div className="absolute bottom-10 left-10 right-10">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 dark:bg-primary/10 border border-white/30 dark:border-primary/20 mb-4">
                <Heart className="w-6 h-6 text-white dark:text-primary" />
              </div>
              <h2 className="font-serif text-3xl italic text-white leading-tight drop-shadow-sm">
                {t("organizerAuth.panelTitle")}
              </h2>
              <p className="text-white/70 text-sm mt-2 drop-shadow-sm">{t("organizerAuth.tagline")}</p>
            </div>
          </div>
          {/* Right form panel */}
          <div className="flex-1 p-12 flex flex-col justify-center bg-background/90">
            <div className="max-w-md mx-auto w-full">
              {formContent}
            </div>
          </div>
        </div>
        <footer className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
          <p className="text-foreground/25 dark:text-white/20 text-xs tracking-widest uppercase">
            {t("organizerAuth.secureGateway")}
          </p>
        </footer>
      </main>
    </div>
  );
}
